import { CopyId } from "./CopyId";
import { t } from "../i18n/strings";
import { getRelativeTime } from "../utils/time";
import type { TicketListItem } from "../api/types";

interface TicketCardProps {
  ticket: TicketListItem;
  onOpen: (ticketId: number) => void;
}

/**
 * Whether the client is the one waiting.
 *
 * Not a status of its own in the database: a third enum value would have to be kept in step by
 * the api and would be wrong the moment it is not. Two timestamps say it without any state.
 */
export function isWaitingForAnswer(ticket: TicketListItem): boolean {
  if (ticket.status !== "open" || !ticket.last_client_message_at) {
    return false;
  }
  if (!ticket.last_support_message_at) {
    return true;
  }
  return new Date(ticket.last_client_message_at) > new Date(ticket.last_support_message_at);
}

/**
 * What to call the client above their id.
 *
 * Empty when telegram gave neither a username nor a name: the id is right there in its own
 * chip, and printing it twice in one line only makes the line harder to read.
 */
export function getClientName(ticket: TicketListItem): string {
  return ticket.username ? `@${ticket.username}` : (ticket.first_name ?? "");
}

/**
 * One request in the list.
 *
 * A div with a button's role rather than a button, because the id inside it copies itself and
 * a button inside a button is not a thing html has. The keyboard handler only answers events
 * the card itself received: the copy button keeps its own space and enter.
 */
export function TicketCard({ ticket, onOpen }: TicketCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      className="ticket-card"
      onClick={() => onOpen(ticket.id)}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onOpen(ticket.id);
        }
      }}
    >
      <div className="ticket-card__head">
        <span className="ticket-card__number">{t("tickets.number", { id: ticket.id })}</span>
        <span className="ticket-card__time">{getRelativeTime(ticket.last_message_at)}</span>
      </div>

      <div className="ticket-card__client">
        {getClientName(ticket) ? <span className="ticket-card__name">{getClientName(ticket)}</span> : null}
        <CopyId value={ticket.user_id} />
        {ticket.language ? <span className="chip chip--language">{ticket.language}</span> : null}
      </div>

      <div className="ticket-card__chips">
        {isWaitingForAnswer(ticket) ? <span className="chip chip--waiting">{t("tickets.waiting")}</span> : null}
        {ticket.client_blocked ? <span className="chip chip--blocked">{t("tickets.blocked")}</span> : null}
        {ticket.is_client === false ? <span className="chip chip--stranger">{t("tickets.notClient")}</span> : null}
        {ticket.is_client === null ? <span className="chip">{t("tickets.unknownClient")}</span> : null}
        <span className="chip chip--quiet">{t("tickets.messages", { count: ticket.messages_count })}</span>
      </div>

      {ticket.preview ? <p className="ticket-card__preview">{ticket.preview}</p> : null}
    </div>
  );
}
